// The catalog's only edit surface (variant B's verdict). Fetches GET /:id so it
// can show how many drivers sit on the row before you retire it.
import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography, TextField, Switch, Button, CircularProgress } from '../../ui/core';
import { hairline } from '../../ui/styles';
import api from '../../lib/api';
import { FIELDS } from './bits';

const EMPTY = { make: '', model: '', year: '', seatCount: '', type: '', isEligible: true, isXl: false };

// The theme's switch leaves the checked track uncolored, and in dark mode
// primary.main is white — a white thumb on a white track, which reads the same
// on as off. Colored when on, neutral gray when off.
const switchSx = (color) => ({
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: color,
    opacity: 1,
  },
});

const numberOrNull = (v) => (v === '' || v == null ? null : Number(v));

const toForm = (vehicle) => ({
  make: vehicle.make ?? '',
  model: vehicle.model ?? '',
  year: vehicle.year ?? '',
  seatCount: vehicle.seatCount ?? '',
  type: vehicle.type ?? '',
  isEligible: vehicle.isEligible,
  isXl: !!vehicle.isXl,
});

const VehiclePanel = ({ vehicle, catalog, onClose }) => {
  const isNew = !vehicle.id;
  const [form, setForm] = useState(isNew ? EMPTY : toForm(vehicle));
  const [driverCount, setDriverCount] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(isNew ? EMPTY : toForm(vehicle));
    setDriverCount(null);
    if (isNew) return;
    let live = true;
    api.vehicles
      .getVehicle(vehicle.id)
      .then((full) => live && setDriverCount(full.driverCount))
      .catch(() => live && setDriverCount(null));
    return () => {
      live = false;
    };
  }, [vehicle, isNew]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const payload = {
    make: form.make.trim(),
    model: form.model.trim(),
    year: Number(form.year),
    seatCount: numberOrNull(form.seatCount),
    type: form.type || undefined,
    isEligible: form.isEligible,
    isXl: form.isXl,
  };

  const valid = payload.make && payload.model && String(form.year).length === 4;

  const submit = async () => {
    setBusy(true);
    const res = isNew ? await catalog.create(payload) : await catalog.save(vehicle.id, payload);
    setBusy(false);
    if (res.success) onClose();
  };

  return (
    <Box
      sx={{
        width: 360,
        flexShrink: 0,
        borderLeft: (t) => `1px solid ${hairline(t)}`,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
      }}
    >
      <Box sx={{ px: 3, py: 2.5, borderBottom: (t) => `1px solid ${hairline(t)}` }}>
        <Typography sx={{ fontSize: '17px', fontWeight: 600, letterSpacing: '-0.01em' }}>
          {isNew ? 'Add vehicle' : `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
        </Typography>
        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mt: 0.25 }}>
          {isNew
            ? 'Adds a make/model/year drivers can pick from'
            : driverCount == null
            ? 'Loading usage…'
            : `${driverCount} driver${driverCount === 1 ? '' : 's'} on this vehicle`}
        </Typography>
      </Box>

      <Stack spacing={2.25} sx={{ px: 3, py: 3, flex: 1, overflowY: 'auto' }}>
        {FIELDS.map((f) => (
          <Box key={f.key}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
              {f.label}
            </Typography>
            <TextField
              value={form[f.key]}
              type={f.type}
              onChange={(e) => set(f.key, e.target.value)}
              fullWidth
              size="small"
            />
          </Box>
        ))}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pt: 1, borderTop: (t) => `1px solid ${hairline(t)}` }}
        >
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Eligible</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
              {form.isEligible ? 'Selectable by drivers' : 'Hidden from the driver picker'}
            </Typography>
          </Box>
          <Switch
            checked={!!form.isEligible}
            onChange={(e) => set('isEligible', e.target.checked)}
            sx={switchSx('success.main')}
          />
        </Stack>

        {!form.isEligible && driverCount > 0 && (
          <Typography sx={{ fontSize: '12px', color: 'warning.main' }}>
            {driverCount} existing driver vehicle{driverCount === 1 ? '' : 's'} stay valid — retiring only
            removes it from new selections.
          </Typography>
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>XL</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
              {form.isXl ? 'Can take XL trips' : 'Standard trips only'}
            </Typography>
          </Box>
          <Switch checked={!!form.isXl} onChange={(e) => set('isXl', e.target.checked)} sx={switchSx('info.main')} />
        </Stack>

        {/* A driver's XL tag is synced from this flag when they change their
            default vehicle, not when the catalog row changes — so flipping it
            here doesn't move drivers already on the vehicle by itself. */}
        {!isNew && form.isXl !== !!vehicle.isXl && driverCount > 0 && (
          <Typography sx={{ fontSize: '12px', color: 'warning.main' }}>
            {driverCount} driver{driverCount === 1 ? '' : 's'} on this vehicle keep their current XL status until
            the XL tag backfill is re-run or they change their default vehicle.
          </Typography>
        )}
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ px: 3, py: 2.5, borderTop: (t) => `1px solid ${hairline(t)}` }}>
        <Button variant="contained" disabled={!valid || busy} onClick={submit} sx={{ flex: 1 }}>
          {busy ? <CircularProgress size={16} color="inherit" /> : isNew ? 'Add to catalog' : 'Save changes'}
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </Stack>
    </Box>
  );
};

export default VehiclePanel;
